import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import { API } from "../App";
import axios from "axios";

const CLVStats = ({ variant = "default" }) => {
  const [clvStats, setClvStats] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clvRes, settingsRes] = await Promise.all([
          axios.get(`${API}/stats/clv`).catch(() => ({ data: null })),
          axios.get(`${API}/settings/site`).catch(() => ({ data: { show_clv_stats: false } }))
        ]);
        setClvStats(clvRes.data);
        setSiteSettings(settingsRes.data);
      } catch (error) {
        console.warn('CLVStats: Failed to fetch stats');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Don't show if toggle is off or no data
  if (loading) return null;
  if (!siteSettings?.show_clv_stats) return null;
  if (!clvStats || clvStats.total_picks_with_clv === 0) return null;

  if (variant === "compact") {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/30">
        <TrendingUp className="w-4 h-4 text-purple-400" />
        <span className="text-sm text-zinc-300">
          <span className="text-purple-400 font-bold">{clvStats.beat_percentage}%</span> Beat Closing Line
        </span>
        <span className={`text-sm font-mono font-bold ${clvStats.average_clv >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          (Avg: {clvStats.average_clv >= 0 ? '+' : ''}{clvStats.average_clv})
        </span>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-xl p-4"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg bg-purple-500/20">
          <TrendingUp className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h4 className="text-white font-semibold">Closing Line Value</h4>
          <p className="text-zinc-500 text-xs">We consistently beat the market</p>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Beat Rate</p>
          <p className="text-2xl font-bold text-purple-400">{clvStats.beat_percentage}%</p>
          <p className="text-zinc-600 text-xs">{clvStats.picks_beating_line}/{clvStats.total_picks_with_clv} picks</p>
        </div>
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Avg CLV</p>
          <p className={`text-2xl font-bold font-mono ${clvStats.average_clv >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {clvStats.average_clv >= 0 ? '+' : ''}{clvStats.average_clv}
          </p>
          <p className="text-zinc-600 text-xs">per pick</p>
        </div>
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Total CLV</p>
          <p className={`text-2xl font-bold font-mono ${clvStats.total_clv >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {clvStats.total_clv >= 0 ? '+' : ''}{clvStats.total_clv}
          </p>
          <p className="text-zinc-600 text-xs">cumulative</p>
        </div>
      </div>
    </motion.div>
  );
};

export default CLVStats;
